import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Box, Images, Map, Video } from 'lucide-react';
import type { StoreProfileDetail } from '../../types';
import { TwinWorkspace } from '../TwinWorkspace';
import { FloorplanTab } from '../tabs/FloorplanTab';
import { StoreImageryTab } from '../tabs/StoreImageryTab';
import { AisleWalkthroughsTab } from '../tabs/AisleWalkthroughsTab';

interface TwinAreaProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  focusedAisleNumber?: number | null;
  onFocusAisle?: (aisleNumber: number | null) => void;
  /** External request to show a specific twin sub-surface (e.g. "workspace") */
  requestedSubTab?: string | null;
  onSubTabChange?: (subTab: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
  manageMode?: boolean;
}

/**
 * Twin area — the default and primary product surface. The workspace holds the
 * floorplan canvas, navigator, detail panel and unmapped inbox; imagery,
 * walk-throughs and floorplan setup are supporting surfaces.
 */
export function TwinArea({
  profile,
  storeId,
  storeIdParam,
  focusedAisleNumber,
  onFocusAisle,
  requestedSubTab,
  onSubTabChange,
  onDirtyChange,
  manageMode = false,
}: TwinAreaProps) {
  const [subTab, setSubTab] = useState('workspace');

  useEffect(() => {
    if (requestedSubTab) {
      setSubTab(requestedSubTab);
    }
  }, [requestedSubTab]);

  const changeSubTab = (value: string) => {
    setSubTab(value);
    onSubTabChange?.(value);
  };

  const unmappedTotal = profile.evidenceMappingStats.needsReview + profile.evidenceMappingStats.unplaced;

  return (
    <Tabs value={subTab} onValueChange={changeSubTab}>
      <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
        <TabsTrigger value="workspace" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Box className="h-4 w-4 mr-1" aria-hidden />Workspace
          {unmappedTotal > 0 && (
            <Badge variant="secondary" className="ml-1.5" aria-label={`${unmappedTotal} unmapped evidence items`}>
              {unmappedTotal}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="imagery" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Images className="h-4 w-4 mr-1" aria-hidden />Store imagery
        </TabsTrigger>
        <TabsTrigger value="walkthroughs" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Video className="h-4 w-4 mr-1" aria-hidden />Walk-throughs
        </TabsTrigger>
        <TabsTrigger value="floorplan" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Map className="h-4 w-4 mr-1" aria-hidden />Floorplan setup
        </TabsTrigger>
      </TabsList>

      <TabsContent value="workspace">
        <TwinWorkspace
          profile={profile}
          storeId={storeId}
          storeIdParam={storeIdParam}
          focusedAisleNumber={focusedAisleNumber}
          onFocusAisle={onFocusAisle}
          onOpenFloorplanSetup={() => changeSubTab('floorplan')}
          onOpenWalkthroughs={() => changeSubTab('walkthroughs')}
          onOpenImagery={() => changeSubTab('imagery')}
          manageMode={manageMode}
        />
      </TabsContent>

      <TabsContent value="imagery">
        <StoreImageryTab profile={profile} storeId={storeId} storeIdParam={storeIdParam} />
      </TabsContent>

      <TabsContent value="walkthroughs">
        <AisleWalkthroughsTab
          profile={profile}
          storeId={storeId}
          storeIdParam={storeIdParam}
          focusedAisleNumber={focusedAisleNumber}
          onFocusAisle={onFocusAisle}
          onOpenDigitalTwin={(aisleNumber) => {
            onFocusAisle?.(aisleNumber);
            changeSubTab('workspace');
          }}
        />
      </TabsContent>

      <TabsContent value="floorplan">
        <FloorplanTab
          profile={profile}
          storeId={storeId}
          storeIdParam={storeIdParam}
          focusedAisleNumber={focusedAisleNumber}
          onFocusAisle={onFocusAisle}
          onDirtyChange={onDirtyChange}
          onOpenDigitalTwin={(aisleNumber) => {
            onFocusAisle?.(aisleNumber);
            changeSubTab('workspace');
          }}
          onOpenWalkthroughs={(aisleNumber) => {
            onFocusAisle?.(aisleNumber);
            changeSubTab('walkthroughs');
          }}
        />
      </TabsContent>
    </Tabs>
  );
}
